import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'src/store';
import { Status as StatusTypes } from 'src/ts/enums';
import { IRejectedAction } from 'src/ts/interfaces';
import { DisputeCategory, SendDisputeResponse } from './api';
import { NAME } from './constants';
import { getDisputeCategories, sendDispute } from './services';

type StateData = {
  categories: DisputeCategory[];
  categoriesStatus: StatusTypes;
  categoriesError: string | null;
  sendDisputeStatus: StatusTypes;
  sendDisputeError: string | null;
  sendDisputeResponse: SendDisputeResponse | null;
};

const initialState: StateData = {
  categories: [],
  categoriesStatus: StatusTypes.IDLE,
  categoriesError: null,
  sendDisputeStatus: StatusTypes.IDLE,
  sendDisputeError: null,
  sendDisputeResponse: null
};

const disputesSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    resetSendDispute: (state) => {
      state.sendDisputeStatus = StatusTypes.IDLE;
      state.sendDisputeError = null;
      state.sendDisputeResponse = null;
    }
  },
  extraReducers: (builder) => {
    getCategoriesReducer(builder);
    sendDisputeReducer(builder);
  }
});

const getCategoriesReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(getDisputeCategories.pending, (state) => {
    state.categoriesStatus = StatusTypes.LOADING;
    state.categoriesError = null;
  });
  builder.addCase(getDisputeCategories.rejected, (state, action: IRejectedAction) => {
    state.categoriesStatus = StatusTypes.ERROR;
    state.categoriesError = action.error?.message || 'Error fetching categories';
  });
  builder.addCase(
    getDisputeCategories.fulfilled,
    (state, action: PayloadAction<DisputeCategory[]>) => {
      state.categoriesStatus = StatusTypes.SUCCESS;
      state.categories = action.payload;
    }
  );
};

const sendDisputeReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(sendDispute.pending, (state) => {
    state.sendDisputeStatus = StatusTypes.LOADING;
    state.sendDisputeError = null;
  });
  builder.addCase(sendDispute.rejected, (state, action: IRejectedAction) => {
    state.sendDisputeStatus = StatusTypes.ERROR;
    state.sendDisputeError = action.error?.message || 'Error submitting dispute';
  });
  builder.addCase(sendDispute.fulfilled, (state, action: PayloadAction<SendDisputeResponse>) => {
    state.sendDisputeStatus = StatusTypes.SUCCESS;
    state.sendDisputeResponse = action.payload;
  });
};

export const { resetSendDispute } = disputesSlice.actions;

export const selectors = {
  categories: (state: RootState) => state.disputes.categories,
  categoriesStatus: (state: RootState) => state.disputes.categoriesStatus,
  categoriesError: (state: RootState) => state.disputes.categoriesError,
  sendDisputeStatus: (state: RootState) => state.disputes.sendDisputeStatus,
  sendDisputeError: (state: RootState) => state.disputes.sendDisputeError,
  sendDisputeResponse: (state: RootState) => state.disputes.sendDisputeResponse
};

export default disputesSlice.reducer;
